
import { useState, useContext, createContext, ReactNode } from 'react';
import { translations } from './translations';

type Language = 'en' | 'es';
type TranslationFunction = (key: string, params?: Record<string, string | number>) => string;

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationFunction;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('en');

  const t: TranslationFunction = (key, params) => {
    // Try to get the translation for the current language
    let translation = translations[language][key];
    
    // Fall back to English if the key doesn't exist in the current language
    if (!translation && language !== 'en') {
      translation = translations['en'][key] || key;
    }
    // If still not found, return the key itself
    else if (!translation) {
      return key;
    }
    
    if (params) {
      let interpolatedTranslation = translation;
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        interpolatedTranslation = interpolatedTranslation.replace(
          new RegExp(`{{${paramKey}}}`, 'g'),
          String(paramValue)
        );
      });
      return interpolatedTranslation;
    }
    
    return translation;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    return {
      language: 'en' as Language,
      setLanguage: () => {},
      t: (key: string) => key,
    };
  }
  return context;
};
